import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { AuthProvider } from '@next-social-platform/chatbot_sso/react';
import { authConfig } from './lib/auth-config';
import { AppGate } from './components/AppGate';
import { BotProvider } from './contexts/BotContext';
import { MeProvider } from './contexts/MeContext';
import { Layout } from './components/Layout';
import { BotWorkspace, LegacyBotRedirect, ScopedRedirect } from './components/BotRedirects';
import { FormsBuilder } from './pages/FormsBuilder';
import { MemoryConfig } from './pages/MemoryConfig';
import { BotConfig } from './pages/BotConfig';
import { BotKnowledge } from './pages/BotKnowledge';
import { BotQuality } from './pages/BotQuality';
import { BotIntents } from './pages/BotIntents';
import { BotReview } from './pages/BotReview';
import { BotModeration } from './pages/BotModeration';
import { ModerationAdmin } from './pages/ModerationAdmin';
import { BotTools } from './pages/BotTools';
import { CacheConfig } from './pages/CacheConfig';
import { Usage } from './pages/Usage';
import { AiConfig } from './pages/AiConfig';
import { LlmPoolControl } from './pages/LlmPoolControl';
import { AgentControl } from './pages/AgentControl';
import { AgentPlannerControl } from './pages/AgentPlannerControl';
import { AgentWorkflowList } from './pages/AgentWorkflowList';
import { AgentWorkflowDetail } from './pages/AgentWorkflowDetail';
import { LlmProviders } from './pages/LlmProviders';
import { EmbeddingProfiles } from './pages/EmbeddingProfiles';
import { TextConversionAdmin } from './pages/TextConversionAdmin';
import { SessionEvents } from './pages/SessionEvents';
import { Hooks } from './pages/Hooks';
import { Eval } from './pages/Eval';
import { IntentImport } from './pages/IntentImport';
import './index.css';

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider config={authConfig} initOptions={{ type: 'login-required' }}>
        <AppGate>
          <MeProvider>
            <BotProvider>
              <Routes>
                <Route element={<Layout />}>
                  <Route index element={<ScopedRedirect section="config" />} />
                  {/* bot workspace: /bots/:system/:botId/:section (hub-ui-step1 spec U1) */}
                  <Route path="/bots/:system/:botId/intents" element={<BotWorkspace><BotIntents /></BotWorkspace>} />
                  <Route path="/bots/:system/:botId/review" element={<BotWorkspace><BotReview /></BotWorkspace>} />
                  <Route path="/bots/:system/:botId/moderation" element={<BotWorkspace><BotModeration /></BotWorkspace>} />
                  <Route path="/bots/:system/:botId/config" element={<BotWorkspace><BotConfig /></BotWorkspace>} />
                  <Route path="/bots/:system/:botId/knowledge" element={<BotWorkspace><BotKnowledge /></BotWorkspace>} />
                  <Route path="/bots/:system/:botId/quality" element={<BotWorkspace><BotQuality /></BotWorkspace>} />
                  <Route path="/bots/:system/:botId/tools" element={<BotWorkspace><BotTools /></BotWorkspace>} />
                  <Route path="/bots/:system/:botId/cache" element={<BotWorkspace><CacheConfig /></BotWorkspace>} />
                  <Route path="/bots/:system/:botId/usage" element={<BotWorkspace><Usage /></BotWorkspace>} />
                  <Route path="/bots/:system/:botId/forms" element={<BotWorkspace><FormsBuilder /></BotWorkspace>} />
                  <Route path="/bots/:system/:botId/memory" element={<BotWorkspace><MemoryConfig /></BotWorkspace>} />
                  {/* pre-step-1 URLs */}
                  <Route path="/bots/:botId/config" element={<LegacyBotRedirect section="config" />} />
                  <Route path="/bots/:botId/tools" element={<LegacyBotRedirect section="tools" />} />
                  <Route path="/bots/:botId/cache" element={<LegacyBotRedirect section="cache" />} />
                  <Route path="/bots/:botId/usage" element={<LegacyBotRedirect section="usage" />} />
                  <Route path="/forms" element={<ScopedRedirect section="forms" />} />
                  <Route path="/memory" element={<ScopedRedirect section="memory" />} />
                  {/* system-wide pages (operate on the active bot where bot-scoped) */}
                  <Route path="/ai-config" element={<AiConfig />} />
                  <Route path="/llm-pool" element={<LlmPoolControl />} />
                  <Route path="/agent" element={<AgentControl />} />
                  <Route path="/agent-planner" element={<AgentPlannerControl />} />
                  <Route path="/agent-workflow" element={<AgentWorkflowList />} />
                  <Route path="/agent-workflow/new" element={<AgentWorkflowDetail />} />
                  <Route path="/agent-workflow/:id" element={<AgentWorkflowDetail />} />
                  <Route path="/llm-providers" element={<LlmProviders />} />
                  <Route path="/embedding-profiles" element={<EmbeddingProfiles />} />
                  <Route path="/text-conversion" element={<TextConversionAdmin />} />
                  {/* /cloud was the old provider page; it read hub's own env and could show a
                      provider as configured while every bot fell back to vLLM. Redirected
                      rather than left to the catch-all, which would land a bookmark on Forms
                      with no hint that the page moved. */}
                  <Route path="/cloud" element={<Navigate to="/llm-providers" replace />} />
                  {/* ops / observability (operate on the active bot) */}
                  <Route path="/session-events" element={<SessionEvents />} />
                  <Route path="/hooks" element={<Hooks />} />
                  <Route path="/eval" element={<Eval />} />
                  <Route path="/intent-import" element={<IntentImport />} />
                  <Route path="/moderation" element={<ModerationAdmin />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
              </Routes>
            </BotProvider>
          </MeProvider>
        </AppGate>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
);
